/**
 * Shared client-side search for the payment processor grids.
 *
 * Why client-side: `payment_processor_config.config` is a Hasura jsonb column,
 * so `_ilike` can't be pointed at connected_account_id /
 * platform_account_location_id — jsonb only supports exact `_contains`
 * matching, which means an ops user would have to paste the account id
 * perfectly with no typos and no partial. Both list loaders already fetch the
 * full row set for the current org scope (the old server-side search never
 * limited the fetch — an empty box pulled everything anyway), so filtering in
 * the browser costs no extra data and buys partial, case-insensitive matching
 * on every field, jsonb ones included.
 *
 * The case this exists for: a merchant onboards to Stripe under their own
 * personal name instead of their business name, so nobody can find the row by
 * name. The connected account id (acct_…) off the Stripe dashboard is then the
 * only handle ops has.
 */

export interface SearchableProcessor {
  id?: number | string | null;
  payment_processor?: string | null;
  processor?: string | null;
  organization?: { name?: string | null } | null;
  config?: Record<string, any> | null;
}

/**
 * Keys inside the jsonb `config` blob that the search box may match against.
 * Deliberately an allow-list rather than "every value in config" so that
 * credentials (secretKey, consumerKey, …) are never searchable.
 */
const SEARCHABLE_CONFIG_KEYS = [
  "connected_account_id",
  "previous_connected_account_id",
  "stripe_deauthorized_account",
  "platform_account_location_id",
  "merchantId",
  "merchantAccountId",
] as const;

const norm = (value: unknown): string =>
  typeof value === "string" || typeof value === "number"
    ? String(value).toLowerCase()
    : "";

/** Every value on a row that the search box is allowed to match against. */
export const processorSearchHaystack = (row: SearchableProcessor): string => {
  const config = (row?.config || {}) as Record<string, any>;
  return [
    norm(row?.id),
    norm(row?.payment_processor),
    norm(row?.processor),
    norm(row?.organization?.name),
    ...SEARCHABLE_CONFIG_KEYS.map((key) => norm(config[key])),
  ]
    .filter(Boolean)
    .join(" ");
};

/**
 * Whitespace-separated terms are ANDed, so "festival acct_1tqm" narrows the
 * result rather than widening it. An empty or whitespace-only query matches
 * every row.
 */
export const matchesProcessorSearch = (
  row: SearchableProcessor,
  search: string
): boolean => {
  const terms = (search || "").toLowerCase().trim().split(/\s+/).filter(Boolean);
  if (terms.length === 0) return true;
  const haystack = processorSearchHaystack(row);
  return terms.every((term) => haystack.includes(term));
};

export const PROCESSOR_SEARCH_PLACEHOLDER =
  "Search name, org, acct_… or tml_…";
